import { Injectable } from '@angular/core';
import { FormStatus } from '../../proxy/forms/form-status.enum';
import { FormPriority } from '../../proxy/forms/form-priority.enum';
import { AdvancedFilters } from '../components/advanced-filter/advanced-filter.component';

// Re-export AdvancedFilters for consumers of FilterService
export { AdvancedFilters };

/**
 * Service for client-side filtering of form data
 * Provides reusable filtering logic for Status, Priority, and Due Date
 */
@Injectable({
  providedIn: 'root'
})
export class FilterService {

  /**
   * Apply client-side filters to data array
   * @param data Array of row objects to filter
   * @param filters Advanced filter settings
   * @param apiFieldList Optional API field list for column detection
   * @returns Filtered array
   */
  applyFilters(data: any[], filters: AdvancedFilters, apiFieldList?: any[]): any[] {
    if (!this.hasActiveFilters(filters)) {
      return data;
    }

    return data.filter(row => {
      // Status filter
      if (filters.statuses.length > 0) {
        const rowStatus = this.getStatusValueFromRow(row, apiFieldList);
        if (rowStatus === null || !filters.statuses.includes(rowStatus)) {
          return false;
        }
      }

      // Priority filter
      if (filters.priorities.length > 0) {
        const rowPriority = this.getPriorityValueFromRow(row, apiFieldList);
        if (rowPriority === null || !filters.priorities.includes(rowPriority)) {
          return false;
        }
      }

      // Due date filter
      if (filters.dueDateOption !== 'all') {
        const rowDueDate = this.getDueDateFromRow(row, apiFieldList);
        if (!this.matchesDueDateFilter(rowDueDate, filters)) {
          return false;
        }
      }

      return true;
    });
  }

  /**
   * Check if any filters are active
   */
  hasActiveFilters(filters: AdvancedFilters): boolean {
    return filters.statuses.length > 0 ||
           filters.priorities.length > 0 ||
           filters.dueDateOption !== 'all';
  }

  /**
   * Extract status enum value from a row
   */
  getStatusValueFromRow(row: any, apiFieldList?: any[]): FormStatus | null {
    // First check the default 'status' property
    if (row.status !== undefined && row.status !== null) {
      return this.parseStatusValue(row.status);
    }

    // Then search through all row properties
    for (const key of Object.keys(row)) {
      const value = row[key];
      
      // Check if this key corresponds to a status field
      if (apiFieldList) {
        const apiField = apiFieldList.find(f => 
          f.fieldOrder?.toString() === key || f.id === key
        );
        if (apiField && (
          apiField.label?.toLowerCase().includes('status') || 
          apiField.fieldName?.toLowerCase().includes('status')
        )) {
          const parsed = this.parseStatusValue(value);
          if (parsed !== null) return parsed;
        }
      }
      
      // Also try to parse string values that look like statuses
      const parsed = this.parseStatusValue(value);
      if (parsed !== null) {
        return parsed;
      }
    }
    
    return null;
  }

  /**
   * Parse a value to FormStatus enum
   */
  private parseStatusValue(value: any): FormStatus | null {
    if (value === undefined || value === null) return null;
    
    // If it's already a number (enum value)
    if (typeof value === 'number' && value >= 0 && value <= 5) {
      return value as FormStatus;
    }
    
    // If it's a string, try to match
    if (typeof value === 'string') {
      const lowerValue = value.toLowerCase().replace(/\s+/g, '');
      if (lowerValue === 'new') return FormStatus.New;
      if (lowerValue === 'committed') return FormStatus.Committed;
      if (lowerValue === 'active') return FormStatus.Active;
      if (lowerValue === 'onhold') return FormStatus.OnHold;
      if (lowerValue === 'cancelled') return FormStatus.Cancelled;
      if (lowerValue === 'done') return FormStatus.Done;
    }
    
    return null;
  }

  /**
   * Extract priority enum value from a row
   */
  getPriorityValueFromRow(row: any, apiFieldList?: any[]): FormPriority | null {
    // First check the default 'priority' property
    if (row.priority !== undefined && row.priority !== null) {
      return this.parsePriorityValue(row.priority);
    }

    // Then search through all row properties
    for (const key of Object.keys(row)) {
      const value = row[key];
      
      // Check if this key corresponds to a priority field
      if (apiFieldList) {
        const apiField = apiFieldList.find(f => 
          f.fieldOrder?.toString() === key || f.id === key
        );
        if (apiField && (
          apiField.label?.toLowerCase().includes('priority') || 
          apiField.fieldName?.toLowerCase().includes('priority')
        )) {
          const parsed = this.parsePriorityValue(value);
          if (parsed !== null) return parsed;
        }
      }
      
      // Also try to parse string values that look like priorities
      const parsed = this.parsePriorityValue(value);
      if (parsed !== null) {
        return parsed;
      }
    }
    
    return null;
  }

  /**
   * Parse a value to FormPriority enum
   */
  private parsePriorityValue(value: any): FormPriority | null {
    if (value === undefined || value === null) return null;
    
    // If it's already a number (enum value)
    if (typeof value === 'number' && value >= 0 && value <= 3) {
      return value as FormPriority;
    }
    
    // If it's a string, try to match
    if (typeof value === 'string') {
      const lowerValue = value.toLowerCase().replace(/\s+/g, '');
      if (lowerValue === 'low') return FormPriority.Low;
      if (lowerValue === 'medium') return FormPriority.Medium;
      if (lowerValue === 'high') return FormPriority.High;
      if (lowerValue === 'veryhigh') return FormPriority.VeryHigh;
    }
    
    return null;
  }

  /**
   * Extract due date from a row
   */
  getDueDateFromRow(row: any, apiFieldList?: any[]): Date | null {
    // First check the default 'dueDate' property
    if (row.dueDate) {
      const parsed = this.parseDate(row.dueDate);
      if (parsed) return parsed;
    }

    // Then search through all row properties
    for (const key of Object.keys(row)) {
      const value = row[key];
      
      if (apiFieldList) {
        const apiField = apiFieldList.find(f => 
          f.fieldOrder?.toString() === key || f.id === key
        );
        
        if (apiField && (
          apiField.label?.toLowerCase().includes('due') || 
          apiField.fieldName?.toLowerCase().includes('due') ||
          apiField.label?.toLowerCase().includes('date')
        )) {
          const parsed = this.parseDate(value);
          if (parsed) return parsed;
        }
      }
    }
    
    return null;
  }

  /**
   * Parse a value to Date
   */
  private parseDate(value: any): Date | null {
    if (!value) return null;
    
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  /**
   * Check if a date matches the due date filter criteria
   */
  matchesDueDateFilter(date: Date | null, filters: AdvancedFilters): boolean {
    if (!date) {
      return filters.dueDateOption === 'all';
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const dateOnly = new Date(date);
    dateOnly.setHours(0, 0, 0, 0);

    switch (filters.dueDateOption) {
      case 'overdue':
        return dateOnly < today;
      
      case 'dueToday':
        return dateOnly.getTime() === today.getTime();
      
      case 'dueThisWeek': {
        const weekEnd = new Date(today);
        weekEnd.setDate(today.getDate() + (7 - today.getDay()));
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        return dateOnly >= weekStart && dateOnly <= weekEnd;
      }
      
      case 'dueThisMonth': {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return dateOnly >= monthStart && dateOnly <= monthEnd;
      }
      
      case 'custom':
        if (filters.customDueDateFrom && filters.customDueDateTo) {
          const from = new Date(filters.customDueDateFrom);
          from.setHours(0, 0, 0, 0);
          const to = new Date(filters.customDueDateTo);
          to.setHours(23, 59, 59, 999);
          return dateOnly >= from && dateOnly <= to;
        }
        return true;
      
      default:
        return true;
    }
  }

  /**
   * Create a default/empty filters object
   */
  createDefaultFilters(): AdvancedFilters {
    return {
      statuses: [],
      priorities: [],
      dueDateOption: 'all',
      customDueDateFrom: undefined,
      customDueDateTo: undefined
    };
  }
}
